import { maybe } from "@saleor/misc";
import { OrderDetails } from "@saleor/motherOrders/types/OrderDetails";
import { RiderDetails } from "@saleor/motherOrders/types/RiderDetails";
import { normalizePhoneNumber } from "@saleor/motherOrders/utils/phone";
import { OrderVerticalType } from "@saleor/types/globalTypes";
import Moment from "moment";

import { getTime } from "./handlers";

export const getInhouseInitialForm = rider => ({
  riderContactNo: maybe(() => rider.order.rider.contactNo),
  riderId: maybe(() => rider.order.rider.id),
  riderLastName: maybe(() => rider.order.rider.lastName),
  riderName: maybe(() => rider.order.rider.firstName)
});

export const getThirdPartyInitialForm = (
  rider: RiderDetails | OrderDetails
) => {
  const order = rider?.order;
  const deliveryCourier = order?.deliveryCourier;
  const dropOffLocation = order?.dropOffLocation;
  const thirdPartyRider = order?.thirdPartyRider;

  return {
    cancelReason: order?.cancelReason ?? "",
    contactPersonContactNo: normalizePhoneNumber(
      deliveryCourier?.senderContactNo ?? dropOffLocation?.contactNo ?? ""
    ),
    contactPersonFirstName:
      deliveryCourier?.senderFirstName ?? dropOffLocation?.firstName ?? "",
    contactPersonLastName:
      deliveryCourier?.senderLastName ?? dropOffLocation?.lastName ?? "",
    courier: order?.courier ?? "",
    dropOffLocationId:
      deliveryCourier?.dropOffLocation?.id ?? dropOffLocation?.id ?? "",
    pickupLatitude:
      deliveryCourier?.dropOffLocation?.latitude.toString() ??
      dropOffLocation?.latitude.toString() ??
      "",
    pickupLocationName:
      deliveryCourier?.dropOffLocation?.dropOffAddress ??
      dropOffLocation?.dropOffAddress ??
      "",
    pickupLongitude:
      deliveryCourier?.dropOffLocation?.longitude.toString() ??
      dropOffLocation?.longitude.toString() ??
      "",
    pickupRemarks:
      deliveryCourier?.dropOffLocation?.remarks ??
      dropOffLocation?.remarks ??
      "",
    rider3plContactNo: thirdPartyRider?.contactNo ?? "",
    rider3plFirstName: thirdPartyRider?.firstName ?? "",
    rider3plLastName: thirdPartyRider?.lastName ?? "",
    rider3plTrackingUrl: thirdPartyRider?.trackingUrl ?? ""
  };
};

export const get2GoManualRebookingData = rider => ({
  airwayBill: maybe(() => rider.order.thirdPartyAwbCode),
  courier: maybe(() => rider.order.courier),
  height: "",
  length: "",
  weight: maybe(() => rider.order.weight.value),
  width: ""
});

export const getTransacInitialForm = rider => ({
  dateDelivered:
    maybe(() => rider.order.completedAt) ||
    Moment(new Date(rider?.order?.created)).format("YYYY-MM-DD"),
  dateDispatched:
    maybe(() => rider.order.shippedAt) ||
    Moment(new Date(rider?.order?.created)).format("YYYY-MM-DD"),
  dwellTime: maybe(() => rider.order.dwellTime),
  onTime: maybe(() => rider.order.onTime),
  orderCreated: maybe(() => rider.order.created),
  timeDelivered: getTime(maybe(() => rider.order.completedAt)),
  timeDispatched: getTime(maybe(() => rider.order.shippedAt)),
  totalTime: maybe(() => rider.order.totalTime)
});

export const transactionInitialErrorValidation = {
  dateErrorMessage: null,
  timeCreatedErrorMessage: null,
  timeErrorMessage: null
  // timeDispatch: null,
  // dateDelivered: null,
  // timeDelivered: null
};

export const initialThirdPartyRiderData = {
  cancelReason: "",
  contactPersonContactNo: "",
  contactPersonFirstName: "",
  contactPersonLastName: "",
  courier: "",
  dropOffAddress: "",
  dropOffLocationId: "",
  isRider3pl: true,
  lateDispatchReason: "",
  lateReason: "",
  pickupLatitude: "",
  pickupLocationName: "",
  pickupLongitude: "",
  pickupRemarks: "",
  rider3plContactNo: "",
  rider3plFirstName: "",
  rider3plLastName: "",
  rider3plTrackingUrl: ""
};

export const initial2GoManualRebookingData = {
  airwayBill: "",
  courier: "",
  height: "",
  length: "",
  weight: "",
  width: ""
};

export const LateDeliveryList = maybe(
  () => [
    {
      label: `Customer - Rescheduled`,
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "RESCHEDULED"
    },
    {
      label: "Customer - Bad Address",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "BAD_ADDRESS"
    },
    {
      label: "Customer - Invalid/Wrong Mobile Number",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "INVALID_MOBILE_NUMBER"
    },
    {
      label: "Customer - No Response",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "NO_RESPONSE"
    },
    {
      label: "Customer - Order Modification",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "ORDER_MODIFICATION"
    },
    {
      label: "Grab - Issue",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "GRAB_ISSUE"
    },
    {
      label: "Ops - Fixed Rider Long Delivery Time",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_FIXED_RIDER"
    },
    {
      label: "Ops - Rider Availability",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_RIDER_AVAILABILITY"
    },
    {
      label: "Ops - Runner Availability",
      type: [OrderVerticalType.FOOD_AND_BEVERAGES],
      value: "OPS_RUNNER_AVAILABILITY"
    },
    {
      label: "Ops - Heavy Rain / Flooded Area",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_HEAVY_RAIN"
    },
    {
      label: "Ops - Tech Issue",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_TECH_ISSUE"
    },
    {
      label: "Ops - Late Fulfillment",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_LATE_FULFILLMENT"
    },
    {
      label: "Tenant - Late Fulfillment",
      type: [OrderVerticalType.GENERAL_MERCHANDISE],
      value: "TENANT_LATE_FULFILLMENT"
    },
    {
      label: "Tenant - Long prep time",
      type: [OrderVerticalType.FOOD_AND_BEVERAGES],
      value: "TENANT_LONG_PREP_TIME"
    },
    {
      label: "Tenant - Out of stock",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_OUT_OF_STOCK"
    },
    {
      label: "Tenant - No Staff In Store / Temporarily Closed",
      type: [OrderVerticalType.FOOD_AND_BEVERAGES],
      value: "TENANT_NO_STAFF_INSTORE"
    },
    {
      label: "Tenant - Price Discrepancy",
      type: [OrderVerticalType.FOOD_AND_BEVERAGES],
      value: "TENANT_PRICE_DISCREPANCY"
    },
    {
      label: "Others",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OTHERS"
    }
  ],
  []
);

export const OpsLateDeliveryList = maybe(
  () => [
    {
      label: "Customer - Bad Address",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "BAD_ADDRESS"
    },
    {
      label: `Customer - Rescheduled`,
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "RESCHEDULED"
    },
    {
      label: "Customer - No Response",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "NO_RESPONSE"
    },

    {
      label: "Customer - Invalid/Wrong Mobile Number",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "INVALID_MOBILE_NUMBER"
    },

    {
      label: "Customer - Order Modification",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "ORDER_MODIFICATION"
    },

    {
      label: "Customer - Late Receiving",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "LATE_RECEIVING"
    },
    {
      label: "Grab - Issue",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "GRAB_ISSUE"
    },
    {
      label: "Grab - Late Pickup",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "GRAB_LATE_PICKUP"
    },
    {
      label: "Grab - Long Delivery Time",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "GRAB_LONG_DELIVERY"
    },
    {
      label: "Ops - Fixed Rider Long Delivery Time",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_FIXED_RIDER"
    },
    {
      label: "Ops - Rider Availability",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_RIDER_AVAILABILITY"
    },
    {
      label: "Ops - Runner Availability",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_RUNNER_AVAILABILITY"
    },
    {
      label: "Ops - Heavy Rain / Flooded Area",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_HEAVY_RAIN"
    },
    {
      label: "Ops - More than 3 Tenant Order",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_MORE_THAN_3"
    },
    {
      label: "Ops - Wrong Pin Location",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_WRONG_PIN"
    },
    {
      label: "Ops - Appsheet / Tech Issue",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_TECH_ISSUE"
    },
    {
      label: "Ops - Late Fulfillment",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OPS_LATE_FULFILLMENT"
    },
    // {
    //   label: "Tenant - Late Fulfillment",
    //   type: [OrderVerticalType.GENERAL_MERCHANDISE],
    //   value: "TENANT_LATE_FULFILLMENT"
    // },
    {
      label: "Tenant - Long prep time",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_LONG_PREP_TIME"
    },
    {
      label: "Tenant - Out of stock",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_OUT_OF_STOCK"
    },
    // {
    //   label: "Tenant - No Staff In Store / Temporarily Closed",
    //   type: [OrderVerticalType.FOOD_AND_BEVERAGES],
    //   value: "TENANT_NO_STAFF_INSTORE"
    // },
    {
      label: "Tenant - Late opening",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_LATE_OPENING"
    },
    {
      label: "Tenant - Issue",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_ISSUE"
    },
    {
      label: "Tenant - Does not fit in the bag",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_DOES_NOT_FIT"
    },
    {
      label: "Tenant - No Staff In Store / Temporarily Closed",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_TEMP_CLOSED"
    },
    {
      label: "Tenant - Price Discrepancy",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "TENANT_PRICE_DISCREPANCY"
    },
    {
      label: "Others",
      type: [
        OrderVerticalType.FOOD_AND_BEVERAGES,
        OrderVerticalType.GENERAL_MERCHANDISE
      ],
      value: "OTHERS"
    }
  ],
  []
);
